import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { enqueueSnackbar } from 'notistack';

import { handleApiCall } from '@/api/utils/handleApiCall';
import { MutationStateFns } from '@/api/utils/mutation';
import { useAuth } from '@/context/AuthProvider';

import { Contact, QUERY_KEY as GET_ALL_QUERY_KEY } from './useGetAllContacts';

export interface CreateContactDetailDto {
  type: string; // 'email' or 'phone'
  value: string;
  label?: string;
  is_primary?: boolean;
}

export interface ApiRequest_Create_Contact {
  name: string;
  user_id?: string;
  role: string;
  metadata?: Record<string, unknown>;
  details?: CreateContactDetailDto[];
  building_ids?: number[];
  unit_ids?: number[];
  location_ids?: number[];
}

export type ApiResponse_Create_Contact = Contact;

const ENDPOINT = 'contacts';
const METHOD = 'POST';

export const useCreateContact = (stateFns?: MutationStateFns<ApiResponse_Create_Contact>) => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ApiRequest_Create_Contact): Promise<ApiResponse_Create_Contact> => {
      const { location_ids, ...contactData } = data;

      // Validate that at least one location is provided
      if (!location_ids || location_ids.length === 0) {
        throw new Error('At least one location is required');
      }

      // Use the first location as the primary location for the API call
      const primaryLocationId = location_ids[0];

      // Include all location_ids in the request body for backend processing
      const requestData = {
        ...contactData,
        location_ids,
      };

      const createdContact = await handleApiCall<ApiResponse_Create_Contact, typeof requestData>(
        session,
        METHOD,
        ENDPOINT,
        requestData,
        {
          params: {
            location_id: primaryLocationId,
          },
        }
      );

      return createdContact;
    },
    onSuccess: (data, variables) => {
      const locationCount = variables.location_ids?.length || 0;
      const message =
        locationCount > 1
          ? `Contact "${data.name}" created successfully and associated with ${locationCount} locations`
          : `Contact "${data.name}" created successfully`;

      enqueueSnackbar(message, { variant: 'success' });

      // Invalidate contacts list for all associated locations
      if (variables.location_ids) {
        variables.location_ids.forEach(locationId => {
          queryClient.invalidateQueries({
            queryKey: GET_ALL_QUERY_KEY(locationId),
          });
        });
      }

      stateFns?.onSuccess?.(data);
    },
    onError: (err: AxiosError) => {
      enqueueSnackbar('Failed to create contact', { variant: 'error' });
      stateFns?.onError?.(err);
    },
  });
};
